import { useMemo } from 'react';
import { SeatingAreaDto, ShiftAvailabilityDto, ShiftSlotDto } from '@/services/platform/api/model';
import { cn } from '@/lib/core/utils/cn';
import { formatTime } from '@/lib/core/utils';
import { useClientTranslation } from '@/lib/core/i18n';
import { useAuthStore } from '@/lib/core/stores';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { Assistance } from '@/components/shared/assistance';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BookShiftTimeSlotsProps {
  seatingAreas: SeatingAreaDto[];
  shifts: ShiftAvailabilityDto[];
  loadingAvailability: boolean;
  selectedRoom: string | null;
  selectedTime: string | null;
  showShifts?: boolean;
  areaLabel?: string;
  slotNotAvailableTitle?: string;
  slotNotAvailableDescription?: string;
  assistance?: { title: string; description: string; descriptionForMember?: string } | null;
  onRoomChange: (room: string | null) => void;
  onTimeClick: (slotTime: string, slot: ShiftSlotDto) => void;
}

// ── BookShiftTimeSlots ────────────────────────────────────────────────────────

export function BookShiftTimeSlots({
  seatingAreas,
  shifts,
  loadingAvailability,
  selectedRoom,
  selectedTime,
  showShifts,
  areaLabel,
  slotNotAvailableTitle,
  slotNotAvailableDescription,
  assistance,
  onRoomChange,
  onTimeClick,
}: BookShiftTimeSlotsProps) {
  const t = useClientTranslation();
  const hasActiveMembership = useAuthStore(s => !!s.user?.hasActiveMembership);

  const resolvedSlotNotAvailableDescription = (
    slotNotAvailableDescription ?? t('App.Labels.SlotNotAvailableDescription')
  ).replace(
    '{{concierge}}',
    hasActiveMembership
      ? t('App.Labels.AssistanceConciergeLabel')
      : t('App.Labels.AssistanceCustomerServiceLabel')
  );

  const displayShifts = useMemo(() => {
    if (showShifts) return shifts;
    const seen = new Set<string>();
    const allSlots = shifts
      .flatMap(s => s.slots ?? [])
      .filter(slot => {
        if (!slot.time || seen.has(slot.time)) return false;
        seen.add(slot.time);
        return true;
      });
    return [{ name: undefined, slots: allSlots }];
  }, [shifts, showShifts]);

  return (
    <div className="flex flex-col gap-6">
      {/* Seating areas — only shown when there are multiple options */}
      {(seatingAreas?.length ?? 0) > 1 && (
        <div className="px-4">
          <p className="font-pp-neue-montreal mb-3 text-[16px] font-medium tracking-[0.25px] text-[#1a1a1a]">
            {areaLabel ?? t('App.Labels.Room')}
          </p>
          <div className="flex flex-wrap gap-4">
            {seatingAreas.map(area => (
              <button
                key={area.publicGuid ?? area.name ?? ''}
                type="button"
                onClick={() => onRoomChange(area.name ?? null)}
                className={cn(
                  'font-pp-neue-montreal h-10 min-w-10 rounded-[20px] px-4 py-2 text-[14px] tracking-[0.5px] transition-colors',
                  selectedRoom === area.name
                    ? 'bg-[#1a1a1a] text-white outline-[0.5px] outline-[#1a1a1a]'
                    : 'text-[#1a1a1a] outline-[0.5px] outline-[#848484]'
                )}
              >
                {area.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Slot info banner + time slots */}
      {loadingAvailability && <SpinLoading wrapperClassName="min-h-0 py-8" />}
      {!loadingAvailability && (
        <>
          {hasActiveMembership && (
            <div className="mx-4 flex items-center gap-4 bg-[#f4f4f4] px-4 py-2">
              <div className="flex flex-1 flex-col gap-0.5">
                <p className="font-pp-neue-montreal text-[12px] font-semibold tracking-[0.5px] text-[#1a1a1a]">
                  {slotNotAvailableTitle ?? t('App.Labels.SlotNotAvailable')}
                </p>
                <p className="font-pp-neue-montreal text-[12px] font-normal tracking-[0.5px] text-[#666]">
                  {resolvedSlotNotAvailableDescription}
                </p>
              </div>
            </div>
          )}

          <div className={cn('flex flex-col px-4', showShifts ? 'gap-6' : 'gap-2')}>
            {displayShifts.map((shift, shiftIndex) => (
              <div
                key={shift.name ?? shiftIndex}
                className={cn('flex flex-col', showShifts ? 'gap-4' : 'gap-2')}
              >
                {showShifts && shift.name && (
                  <p className="font-pp-neue-montreal text-[16px] font-medium tracking-[0.25px] text-[#1a1a1a]">
                    {shift.name}
                  </p>
                )}
                <div className="grid grid-cols-4 gap-2">
                  {(shift.slots ?? []).map(slot => {
                    const slotTime = slot.time ?? '';
                    let unavailable = !slot.isAvailable;

                    const currentAreaGuid = seatingAreas.find(a => a.name === selectedRoom)?.publicGuid;

                    const matchedArea =
                      selectedRoom && currentAreaGuid
                        ? slot?.availableSeatingAreas?.find(a => a.publicGuid === currentAreaGuid)
                        : undefined;

                    const firstBookArea = !matchedArea
                      ? slot.availableSeatingAreas?.find(a => a.type === 'book')
                      : undefined;

                    if (matchedArea) {
                      unavailable = matchedArea.type == 'request';
                    } else if (firstBookArea) {
                      unavailable = false;
                    } else {
                      unavailable = true;
                    }

                    console.log(
                      slotTime,
                      selectedRoom,
                      currentAreaGuid,
                      matchedArea,
                      firstBookArea,
                      unavailable,
                      hasActiveMembership
                    );

                    const disabled = unavailable && !hasActiveMembership;
                    return (
                      <button
                        key={slotTime}
                        type="button"
                        disabled={disabled}
                        onClick={() => onTimeClick(slotTime, slot)}
                        className={cn(
                          'font-pp-neue-montreal w-full rounded-[2px] px-2 py-4 text-center text-[14px] tracking-[0.5px] transition-colors',
                          selectedTime === slotTime && !unavailable
                            ? 'bg-[#1a1a1a] text-white outline-[0.5px] outline-[#1a1a1a]'
                            : unavailable
                              ? 'text-[#bababa] outline-[0.5px] outline-[#eaeaea]'
                              : 'text-[#4e4e4e] outline-[0.5px] outline-[#eaeaea]',
                          disabled && 'cursor-not-allowed'
                        )}
                      >
                        {formatTime(slotTime)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {assistance && <Assistance {...assistance} className="flex flex-col gap-6 px-4 pt-4 pb-6" />}
    </div>
  );
}
